import {
  ApplePay,
  Cart,
  ShippingMethod,
} from '../../../interface/checkoutInterface';
import { Shop } from '../../../interface/checkoutInterface';
import { EventPublisher } from '../../../utils/publishEvent';
import checkoutService from '../../../api/checkoutService';
import { ExtractPhxRequestId } from '../../../utils/extractPhoenixId';
import {
  countryCurrencyList,
  getIsStaging,
  getIsViewIsEdit,
} from '../../../utils/constants';
import { CountryWithStateList } from '../../../data/country';
import {
  CheckoutCompletePayload,
  IDoVGSDecryptForGPay,
  SetContactInfoPayload,
  SetShippingAddressPayload,
} from '../../../interface/apiServiceInterface';
import { v4 as uuidv4 } from 'uuid';
import { setSalesTax } from '../../../redux/slices/cartSlice';
import { Dispatch } from 'redux';
import { UseFormSetValue } from 'react-hook-form';
import { AddressFormType } from '../../../interface/formInterface';
import { setAvailableShippingMethods } from '../../../redux/slices/checkoutSlice';

// Extend the Window interface to include ApplePaySession
declare global {
  interface Window {
    ApplePaySession: {
      new (_version: number, _request: object): ApplePaySession;
      applePayCapabilities(
        _merchantIdentifier: string,
      ): Promise<ApplePayCapabilities>;
      canMakePayments?: () => boolean;
      supportsVersion?: (_version: number) => boolean;
      STATUS_SUCCESS: number;
      STATUS_FAILURE: number;
      STATUS_INVALID_BILLING_POSTAL_ADDRESS: number;
      STATUS_INVALID_SHIPPING_POSTAL_ADDRESS: number;
      STATUS_INVALID_SHIPPING_CONTACT: number;
      STATUS_PIN_REQUIRED: number;
      STATUS_PIN_INCORRECT: number;
      STATUS_PIN_LOCKOUT: number;
    };
  }

  interface ApplePayCapabilities {
    paymentCredentialStatus?: string;
  }

  interface ApplePayShippingContactEvent {
    shippingContact: {
      countryCode: string;
      postalCode: string;
      locality: string;
      administrativeArea: string;
    };
  }

  interface ApplePaySession {
    onvalidatemerchant: (_event: { validationURL: string }) => void;
    onpaymentauthorized: (_event: {
      payment: {
        token: eventToken;
        shippingContact: {
          emailAddress: string;
          phoneNumber: string;
          addressLines: string[];
          locality: string;
          countryCode: string;
          givenName: string;
          familyName: string;
          administrativeArea: string;
          postalCode: string;
        };
        billingContact: {
          givenName: string;
          familyName: string;
          emailAddress: string;
          phoneNumber: string;
          addressLines: string[];
          locality: string;
          countryCode: string;
          administrativeArea: string;
        };
      };
      complete: (_status: number) => void;
    }) => void;
    onshippingcontactselected: (_event: ApplePayShippingContactEvent) => void;
    completeShippingContactSelection: (_result: {
      newTotal: { label: string; amount: string };
      newLineItems?: { label: string; amount: string }[];
    }) => void;
    completeMerchantValidation: (_merchantSession: number) => void;
    completePayment: (_status: number) => void;
    begin: () => void;
    oncancel: () => void;
    abort: () => void;
  }
}
const convertToMMYY = (dateStr: string): string => {
  if (!/^\d{6}$/.test(dateStr)) {
    throw new Error('Invalid date format. Expected yymmdd.');
  }

  const year = dateStr.slice(0, 2);
  const month = dateStr.slice(2, 4);

  return `${month}/${year}`;
};

interface applePaymentEvent {
  payment: {
    token: eventToken;
    shippingContact: {
      emailAddress: string;
      phoneNumber: string;
      addressLines: string[];
      locality: string;
      countryCode: string;
      givenName: string;
      familyName: string;
      administrativeArea: string;
      postalCode: string;
    };
    billingContact: {
      givenName: string;
      familyName: string;
      emailAddress: string;
      phoneNumber: string;
      addressLines: string[];
      locality: string;
      countryCode: string;
      administrativeArea: string;
    };
  };
  complete: (_status: number) => void;
}

export interface eventToken {
  transactionIdentifier: string;
  paymentData: {
    data: string;
    signature: string;
    header: {
      publicKeyHash: string;
      ephemeralPublicKey: string;
      transactionId: string;
    };
    version: string;
  };
  paymentMethod: {
    displayName: string;
    network: string;
    type: string;
  };
}

const publish = EventPublisher.getInstance().publish;

export const startApplePay = async (
  Apple: ApplePay,
  cartData: Cart,
  completeApi: (
    _data: CheckoutCompletePayload,
    _session: ApplePaySession,
  ) => void,
  setCartDataRef: React.MutableRefObject<Cart | null>,
  storeData: Shop,
  isTermsAccepted: boolean,
  dispatch: Dispatch,
  setValue: UseFormSetValue<AddressFormType>,
  IsSMSConsent: boolean,
  IsEmailConsent: boolean,
  igParams?: string | null,
  pageId?: string | null,
  currentPageFunnelId?: string | null,
) => {
  let paymentPayload: object = {};
  let logResponse: Record<string, string> = {};
  const paymentMethods = [];

  if (storeData?.EnableAmexCard) paymentMethods.push('amex');
  if (storeData?.EnableMasterCard) paymentMethods.push('masterCard');
  if (storeData?.EnableVisaCard) paymentMethods.push('visa');
  if (storeData?.EnableDiscoverCard) paymentMethods.push('discover');

  try {
    const paymentRequest = {
      countryCode:
        countryCurrencyList.find(
          item => item.currencyCode === cartData?.currencyCode,
        )?.countryCode || 'US',
      currencyCode: cartData?.currencyCode || 'USD',
      total: { label: Apple?.StoreTitle, amount: cartData?.TotalAmount },
      supportedNetworks: paymentMethods,
      merchantCapabilities: ['supports3DS'],
      requiredShippingContactFields: [
        'postalAddress',
        'email',
        'phone',
        'name',
      ],
    };

    const session = new window.ApplePaySession(3, paymentRequest);
    session.onshippingcontactselected = async event => {
      try {
        const shippingContact = event.shippingContact;
        const ProvinceCode = CountryWithStateList.find(
          country => country.code === shippingContact?.countryCode,
        )?.provinces.find(
          state => state.name === shippingContact?.administrativeArea,
        )?.code;
        const Address = {
          FirstName: '',
          LastName: '',
          Address1: '',
          Address2: '',
          Organization: '',
          City: shippingContact?.locality,
          CountryCode: shippingContact?.countryCode,
          ProvinceCode: ProvinceCode || shippingContact?.administrativeArea,
          Zip: shippingContact?.postalCode,
        };
        const shippingPayload = {
          Address: { ...Address, ForApplePaySalesTax: true },
          ID: cartData.ID,
          ShopID: storeData.ShopID,
          StoreID: +(storeData?.StoreID ?? 0),
          PageID: String(pageId) || '',
        };
        const response = await updateShippingAddress(
          shippingPayload,
          storeData.ShopID,
          setCartDataRef,
        );
        if (response) {
          setValue(`Shipping`, Address);
          dispatch(
            setAvailableShippingMethods(
              response?.AvailableShippingMethods as ShippingMethod[],
            ),
          );
          dispatch(
            setSalesTax({
              SalesTaxAmount: response?.SalesTaxAmount,
              TotalAmount: response?.TotalAmount,
              EstimatedTaxAmount: response?.EstimatedTaxAmount,
              Fees: response?.Fees,
              DiscountAmount: response?.DiscountAmount,
              SubtotalAmount: response?.SubtotalAmount,
              ShippingRequired: response?.ShippingRequired,
              ShippingMethodID: response?.ShippingMethodID,
            }),
          );

          session.completeShippingContactSelection({
            newTotal: {
              label: Apple?.StoreTitle,
              amount: response?.TotalAmount,
            },
            // newLineItems: [{ label: 'Subtotal', amount: response?.SubtotalAmount }],
          });
        }
      } catch (error) {
        console.error('Error in onshippingcontactselected function', error);
        session.completeShippingContactSelection({
          newTotal: {
            label: Apple?.StoreTitle,
            amount: cartData?.TotalAmount,
          },
        });
      }
    };

    session.onvalidatemerchant = async (event: { validationURL: string }) => {
      try {
        // Call your server to validate merchant
        const merchantSession = await checkoutService.doMerchantValidation({
          validationURL: event.validationURL,
          storeId: +cartData.StoreID,
        });
        session.completeMerchantValidation(merchantSession?.data?.Result);
      } catch (error) {
        console.error('Error in onvalidatemerchant function', error);
        // setCheckoutErrorMessage(error?.message);
        session.abort();
      }
    };

    session.onpaymentauthorized = async (event: applePaymentEvent) => {
      try {
        if (getIsViewIsEdit()) {
          session.completePayment(window.ApplePaySession?.STATUS_SUCCESS ?? 0);
          return;
        }
        paymentPayload = event.payment.token;
        const response = await fetch(
          `https://${Apple?.VaultId}${Apple?.RouteId ? '-' + Apple?.RouteId : ''}${getIsStaging() ? '.sandbox' : '.live'}.verygoodproxy.com/post`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: paymentPayload }),
          },
        ).then(res => res.json());
        logResponse = response;

        const applicationExpirationDate = convertToMMYY(
          response?.json?.apple_pay_token?.token?.paymentData?.data
            ?.applicationExpirationDate,
        );
        const applicationPrimaryAccountNumber =
          response?.json?.apple_pay_token?.token?.paymentData?.data
            ?.applicationPrimaryAccountNumber;
        const cavv =
          response?.json?.apple_pay_token?.token?.paymentData?.data?.paymentData
            ?.onlinePaymentCryptogram;
        const eci =
          response?.json?.apple_pay_token?.token?.paymentData?.data?.paymentData
            ?.eciIndicator;

        // setcontactinfo
        const contactInfoPayload = {
          Email: event?.payment?.shippingContact?.emailAddress || '',
          PhoneNumber: event?.payment?.shippingContact?.phoneNumber || '',
          ID: cartData.ID,
          ShopID: storeData.ShopID,
          StoreID: (storeData.StoreID || 0) as number,
        };
        await updateContactInfo(contactInfoPayload, storeData.ShopID);

        //setshippinginfo
        const shippingPayload = {
          Address: {
            Address1: event?.payment?.shippingContact?.addressLines[0],
            Address2: event?.payment?.shippingContact?.addressLines[1],
            City: event?.payment?.shippingContact?.locality,
            CountryCode: event?.payment?.shippingContact?.countryCode,
            FirstName: event?.payment?.shippingContact?.givenName,
            LastName:
              event?.payment?.shippingContact?.familyName ||
              event?.payment?.shippingContact?.givenName,
            Organization: '',
            ProvinceCode: event?.payment?.shippingContact?.administrativeArea,
            Zip: event?.payment?.shippingContact?.postalCode,
          },
          ID: cartData.ID,
          ShopID: storeData.ShopID,
          StoreID: (storeData.StoreID || 0) as number,
          PageID: String(pageId) || '',
        };
        await updateShippingAddress(
          shippingPayload,
          storeData.ShopID,
          setCartDataRef,
        );
        const ProvinceCode = CountryWithStateList.find(
          country =>
            country.code === event?.payment?.shippingContact?.countryCode,
        )?.provinces.find(
          state =>
            state.name === event?.payment?.shippingContact?.administrativeArea,
        )?.code;
        await completeApi(
          {
            BillingAddress: {
              Address1: event?.payment?.shippingContact?.addressLines[0],
              Address2: event?.payment?.shippingContact?.addressLines[1],
              City: event?.payment?.shippingContact?.locality,
              Zip: event?.payment?.shippingContact?.postalCode,
              CountryCode: event?.payment?.shippingContact?.countryCode,
              ProvinceCode:
                ProvinceCode ||
                event?.payment?.shippingContact?.administrativeArea,
              Organization: '',
              FirstName: event?.payment?.billingContact?.givenName,
              LastName:
                event?.payment?.billingContact?.familyName ||
                event?.payment?.billingContact?.givenName,
            },
            CardInfo: {
              Number: applicationPrimaryAccountNumber,
              Exp: applicationExpirationDate,
              NameOnCard: '',
              ApplePay: { cavv, eci },
            },
            ID: cartData.ID,
            IsTermsChecked: isTermsAccepted,
            IsSMSConsent,
            IsEmailConsent,
            ShopID: storeData.ShopID,
            StoreID: Number(storeData?.StoreID),
            CurrentPageFunnelId: currentPageFunnelId || '',
            PageId: String(pageId) || '',
            applePayData: response?.json,
            igParams: igParams ? JSON.parse(atob(igParams || '{}')) : undefined,
          },
          session,
        );

        session.completePayment(window.ApplePaySession?.STATUS_SUCCESS ?? 0);
      } catch (error) {
        console.error('Error in onpaymentauthorized function', error);
      } finally {
        if (!getIsViewIsEdit()) {
          await checkoutService.applePayLog({
            storeId: +cartData?.StoreID,
            cartId: +cartData?.ID,
            environment: process.env.NEXT_PUBLIC_ENVIRONMENT || '',
            clientId: +(cartData?.clientID || 0),
            request: paymentPayload,
            response: logResponse,
            cartUrl: window.location.href,
          });
        }
      }
    };
    session.begin();
  } catch (error) {
    console.error('Error in startApplePay function', error);
  }
};

export const updateContactInfo = async (
  payload: SetContactInfoPayload,
  shopId: string,
) => {
  try {
    const contactRes = await checkoutService.setContactInfo(payload);
    if (contactRes.data) {
      publish('CustomerContact', {
        eventName: 'CustomerContact',
        eventId: ExtractPhxRequestId(contactRes),
        shopId,
        cart: contactRes.data.Cart,
      });
    }
  } catch (error) {
    console.error('Error setting contact info:', error);
  }
};

export const updateShippingAddress = async (
  payload: SetShippingAddressPayload,
  shopId: string,
  setCartDataRef: React.MutableRefObject<Cart | null>,
) => {
  try {
    const uniqueEventId = uuidv4();
    const shippingRes = await checkoutService.setShippingAddress(
      payload,
      uniqueEventId,
    );
    if (shippingRes.data) {
      publish('ShippingAdded', {
        eventId: uniqueEventId,
        eventName: 'ShippingAdded',
        shopId,
        cart: shippingRes.data.Cart,
      });
      publish('AddShippingInfo', {
        eventName: 'AddShippingInfo',
        eventId: uniqueEventId,
        shopId,
        cart: shippingRes.data.Cart,
      });
      const updatedCart = {
        ...shippingRes?.data?.Cart,
        Email: shippingRes.data?.Cart.Email,
        PhoneNumber: shippingRes.data?.Cart.PhoneNumber,
      };
      setCartDataRef.current = updatedCart;
      return updatedCart;
    }
  } catch (error) {
    console.error('Error setting shipping address:', error);
  }
};

export const callVGSDecrypt = async (
  payload: IDoVGSDecryptForGPay,
  vaultId: string,
  routeId: string,
  // isSandbox?: boolean = false,
) => {
  try {
    const { data } = await checkoutService.doVGSDecrypt(
      `${vaultId}${routeId ? '-' + routeId : ''}`,
      payload,
      // isSandbox,
    );
    return JSON.parse(data);
  } catch (error) {
    console.error('Error in VGS Decrypt:', error);
  }
};

export const getProvinceCode = (
  address:
    | {
        countryCode: string;
        administrativeArea: string;
      }
    | undefined,
) => {
  return CountryWithStateList.find(
    country => country.code === address?.countryCode,
  )?.provinces.find(state => state.name === address?.administrativeArea)?.code;
};

export const splitName = (name: string) => {
  const [firstName, ...rest] = name.trim().split(' ');
  return { firstName, lastName: rest.join(' ') };
};
